import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { WakzyMark } from "./WakzyLogo";
import {
  LayoutDashboard,
  Car,
  Users,
  UserCircle,
  Wallet,
  FileText,
  FolderOpen,
  Wrench,
  BarChart3,
  UserCog,
  LogOut,
  Search,
  Gavel,
  Tablet,
} from "lucide-react";
import { Input } from "./ui/input";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin/inventory", label: "Inventory", icon: Car },
  { to: "/admin/crm", label: "CRM Pipeline", icon: Users },
  { to: "/admin/customers", label: "Customers", icon: UserCircle },
  { to: "/admin/documents", label: "Documents / IDs", icon: FolderOpen },
  { to: "/admin/deals", label: "Deals", icon: FileText },
  { to: "/admin/finance", label: "Finance Desk", icon: Wallet },
  { to: "/admin/service", label: "Service", icon: Wrench },
  { to: "/admin/auctions", label: "Auctions", icon: Gavel },
  { to: "/admin/users", label: "Users", icon: UserCog },
  { to: "/admin/reports", label: "Reports", icon: BarChart3 },
];

export function AdminShell({ children, title }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (item) =>
    item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  return (
    <div className="min-h-screen flex" style={{ background: "var(--wakzy-bg)" }}>
      {/* Sidebar */}
      <aside className="w-64 surface border-r border-white/5 flex flex-col" data-testid="admin-sidebar">
        <div className="p-5 border-b border-white/5">
          <div className="logo-glow inline-block">
            <WakzyMark size={42} />
          </div>
          <div className="mt-3 label-eyebrow gold-text font-bold">DealerOS</div>
        </div>
        <nav className="flex-1 py-4">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`sidebar-link ${isActive(item) ? "active" : ""}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="label-eyebrow mb-2" style={{ color: "var(--wakzy-gold)" }}>Wakzy Network</div>
          <a
            href="https://www.wakzyauto.com"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-xs text-slate-300 hover:text-[#D4AF37] py-1"
            data-testid="admin-link-wakzyauto"
          >
            wakzyauto.com <span className="text-[9px] text-slate-500">↗</span>
          </a>
          <a
            href="https://www.wakzyautotech.com"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-xs text-slate-300 hover:text-[#D4AF37] py-1"
            data-testid="admin-link-wakzyautotech"
          >
            wakzyautotech.com <span className="text-[9px] text-slate-500">↗</span>
          </a>
        </div>
        <div className="p-4 border-t border-white/5">
          <div className="text-xs text-slate-400">{user?.name}</div>
          <div className="label-eyebrow mt-1" style={{ color: "var(--wakzy-gold)" }}>
            {user?.role?.replace("_", " ")}
          </div>
          <button
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="mt-3 flex items-center gap-2 text-sm text-slate-400 hover:text-white"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <header className="surface border-b border-white/5 px-8 py-4 flex items-center justify-between">
          <div>
            <div className="brand-divider max-w-[200px]">
              <span className="dot" />
              <span className="label-eyebrow">Wakzy DealerOS</span>
            </div>
            <h1 className="font-heading text-2xl font-medium mt-1 chrome-text inline-block">{title}</h1>
          </div>
          <div className="flex items-center gap-3 w-1/3">
            <button
              onClick={() => {
                try { sessionStorage.removeItem("wakzy_kiosk_unlocked"); } catch { /* ignore */ }
                window.open("/kiosk-launch", "_blank", "noopener");
              }}
              className="inline-flex items-center gap-2 px-3 h-9 rounded-sm border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs font-medium whitespace-nowrap transition"
              data-testid="admin-launch-kiosk-btn"
              title="Open Tablet Kiosk in a new tab (requires PIN)"
            >
              <Tablet className="w-3.5 h-3.5" /> Launch Kiosk
            </button>
            <div className="relative w-full">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                placeholder="Search vehicles, leads, deals..."
                className="pl-10 bg-[#04060d] border-white/10"
                data-testid="admin-global-search"
              />
            </div>
          </div>
        </header>
        <div className="flex-1 p-8 overflow-auto">{children}</div>
      </main>
    </div>
  );
}
